import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { handleError } from 'shared/api/lib/errors/handlers'
import { AiEngagementGoalApi } from 'shared/api/requests/aiEngagementGoal'
import { accountNotificationsListener } from 'utils/services/notificationsService'
import { SocketEventBaseSchema } from 'utils/services/notificationsService/constants'

const messageGeneratedEventSchema = SocketEventBaseSchema.extend({
  event: z.literal('ai_engagement_goal_lead_gen_message_generated'),
  goal_id: z.string(),
  message: z.string(),
  button_title: z.string(),
})

export const useContentGenerator = ({
  onGenerate,
}: {
  onGenerate: (data: { message: string; buttonTitle: string }) => void
}) => {
  const [isLoading, setisLoading] = useState(false)
  const goalIdRef = useRef<string | null>(null)

  const generateContent = useCallback(
    async ({ goalId, url, context }: { goalId: string; context: string; url: string }) => {
      try {
        setisLoading(true)

        goalIdRef.current = goalId

        await AiEngagementGoalApi.generateLeadGenMessage({
          body: { goal_id: goalId, context, url },
        })
      } catch (error) {
        goalIdRef.current = null
        setisLoading(false)
        handleError(error)
      }
    },
    [],
  )

  const handleGenerationEvent = useCallback(
    (data: { goal_id: string; message: string; button_title: string }) => {
      if (data.goal_id === goalIdRef.current) {
        onGenerate({ message: data.message, buttonTitle: data.button_title })
        setisLoading(false)
        goalIdRef.current = null
      }
    },
    [onGenerate],
  )

  useEffect(() => {
    accountNotificationsListener.on(messageGeneratedEventSchema, handleGenerationEvent)

    return () => {
      accountNotificationsListener.removeListener(
        messageGeneratedEventSchema,
        handleGenerationEvent,
      )
    }
  }, [handleGenerationEvent])

  return {
    isLoading,
    generateContent,
  }
}
